import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

interface NavLink {
  label: string;
  path: string;
  external?: boolean;
  children?: NavLink[];
}

const navLinks: NavLink[] = [
  { label: '首頁', path: '/home' },
  { label: '品牌介紹', path: '/about' },
  { label: '合作通路', path: '/stores' },
  { label: '常見問題', path: '/faq' },
  { label: '閱讀推薦', path: '/blog' },
  { label: '書籍資料庫', path: 'https://catalog.sonbook.net/', external: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  const isHome = useMemo(() => location.pathname === '/home', [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setScrolled(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location]);

  const navBg = scrolled
    ? 'bg-primary-500/15 backdrop-blur-md border-b border-primary-300/20'
    : 'bg-transparent';

  const linkColor = isHome ? 'text-background-50' : 'text-foreground-900';
  const linkMuted = isHome ? 'text-background-50/70' : 'text-foreground-700';
  const linkHover = isHome ? 'hover:text-background-50' : 'hover:text-foreground-950';

  const dropdownBg = isHome
    ? 'bg-primary-600 border-primary-400/30'
    : 'bg-background-50/95 backdrop-blur-md border-background-200 shadow-lg';
  const dropdownItem = isHome
    ? 'text-background-50/80 hover:bg-primary-500 hover:text-background-50'
    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900';

  const mobileBg = isHome
    ? 'bg-primary-600/95 border-primary-400/20'
    : 'bg-background-50/98 backdrop-blur-md border-background-200/60';
  const mobileLabel = isHome ? 'text-background-50/50' : 'text-foreground-500';
  const mobileLink = isHome
    ? 'text-background-50/80 hover:text-background-50'
    : 'text-foreground-700 hover:text-foreground-900';
  const mobileActive = isHome ? 'text-background-50' : 'text-foreground-900';

  const hamburgerColor = isHome ? 'text-background-50' : 'text-foreground-900';

  const renderNavItem = (link: NavLink, isMobile: boolean) => {
    const isActive = !link.external && location.pathname === link.path;
    const baseClass = isMobile
      ? `py-2 text-sm transition-colors duration-150 whitespace-nowrap cursor-pointer ${isActive ? `font-medium ${mobileActive}` : mobileLink}`
      : `transition-all duration-200 whitespace-nowrap cursor-pointer ${isActive ? `${linkColor} font-medium` : `${linkMuted} ${linkHover}`} ${scrolled ? 'text-xs px-3 py-2' : 'text-sm px-4 py-2'}`;

    if (link.external) {
      return (
        <a
          key={link.label}
          href={link.path}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className={baseClass}
        >
          <LocalizedNavLabel href={link.path} fallback={link.label} />
          <i className="ri-external-link-line ml-1 text-[10px]"></i>
        </a>
      );
    }

    return (
      <Link
        key={link.path}
        to={link.path}
        className={baseClass}
      >
        <LocalizedNavLabel href={link.path} fallback={link.label} />
      </Link>
    );
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="w-full px-6 md:px-10">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-12 md:h-14' : 'h-16 md:h-20'}`}>
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <img
              src="https://storage.readdy-site.link/project_files/5a105479-8d38-448e-9522-af2f638a0e72/43972714-abc7-46e0-b45a-08a831bb46bf_logo-02-01.png"
              alt="清文華泉事業有限公司 Cinva"
              className={`transition-all duration-300 rounded-sm ${scrolled ? 'h-7 w-auto' : 'h-9 w-auto'}`}
            />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    className={`px-4 py-2 text-sm ${linkMuted} ${linkHover} transition-all duration-200 flex items-center gap-1 whitespace-nowrap cursor-pointer ${scrolled ? 'text-xs' : ''}`}
                  >
                    <LocalizedNavLabel href={link.path} fallback={link.label} />
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                  {activeDropdown === link.label && (
                    <div className={`absolute top-full left-0 mt-1 border rounded-lg py-2 min-w-[180px] ${dropdownBg}`}>
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 whitespace-nowrap ${dropdownItem}`}
                        >
                          <LocalizedNavLabel href={child.path} fallback={child.label} />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                renderNavItem(link, false)
              )
            )}
          </div>

          <LanguageSwitcher />

          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${hamburgerColor}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={`md:hidden border-t ${mobileBg}`}>
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <div className={`py-2 text-sm font-medium ${mobileLabel}`}>
                    <LocalizedNavLabel href={link.path} fallback={link.label} />
                  </div>
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`block pl-4 py-2 text-sm transition-colors duration-150 whitespace-nowrap ${mobileLink}`}
                    >
                      <LocalizedNavLabel href={child.path} fallback={child.label} />
                    </Link>
                  ))}
                </div>
              ) : (
                renderNavItem(link, true)
              )
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
